import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsCart4 } from "react-icons/bs";
import { MdCreditCard } from "react-icons/md";
import { GiHemp } from "react-icons/gi";
import { FaEdit } from "react-icons/fa";
import { FaDeleteLeft } from "react-icons/fa6";
import { HiOutlineClock } from "react-icons/hi";
import ClientTransactions from "../DispenserComponents/ClientTransactions";
import { getImageCacheKey, getImageUrl } from '../../../lib/helpers';
import CustomModal from '../../Elements/CustomModal';

function getClientProfileImagePath(profile) {
  return profile?.documents_path || profile?.image_path || null;
}

function ClientDetailModal({
  active,
  handleModal,
  client,
  token,
  venueID,
  onEdit,
  onDelete,
  onAddCredit,
  isClientInVenue = false,
  /** true cuando el modal se abre desde Dispenser (ruta /dispenser o incrustado en Entradas) */
  dispenserContext = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isInDispenser =
    dispenserContext || location.pathname.includes('/dispenser');
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const fetchTransactions = useCallback(async () => {
    if (!token || !client?.id) return;

    setLoadingTransactions(true);
    try {
      const response = await fetch('/v1/get-all-transactions', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al obtener transacciones');
      }

      const data = await response.json();
      const txList = Array.isArray(data.transactions) ? data.transactions : (Array.isArray(data) ? data : []);
      setTransactions(txList);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [token, client?.id]);

  useEffect(() => {
    if (showHistory && active && client?.id) {
      fetchTransactions();
    }
  }, [showHistory, active, client?.id, fetchTransactions]);

  useEffect(() => {
    if (!active) {
      setShowHistory(false);
      setTransactions([]);
    }
  }, [active]);

  if (!active || !client) return null;

  const imageUrl = getImageUrl(getClientProfileImagePath(client.profile), {
    cacheKey: getImageCacheKey('client', client.id),
  });

  const displayName = client.profile?.first_name && client.profile?.last_name
    ? `${client.profile.first_name} ${client.profile.last_name}`
    : client.email;

  const _exp = client.profile?.expiration_date ? new Date(client.profile.expiration_date) : null;
  const estado = (_exp && _exp < new Date(new Date().toDateString())) ? 'Caducado' : 'Activo';

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (/^\d{4}-\d{2}-\d{2}/.test(String(dateString))) {
      const [y, m, d] = String(dateString).split('T')[0].split('-').map(Number);
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const consumptionLabel = () => {
    const lud = client.profile?.consumption_type?.ludenos;
    const ter = client.profile?.consumption_type?.terap;
    if (!lud && !ter) return '-';
    return [lud ? 'Lúd.' : '', ter ? 'Terap.' : ''].filter(Boolean).join(' ');
  };

  const modalTitle = showHistory ? 'Historial de movimientos' : 'Datos del Socio';

  const closeOrBack = () => {
    if (showHistory) setShowHistory(false);
    else handleModal();
  };

  const requireClientInVenue = () => {
    if (isClientInVenue || isInDispenser) return true;
    window.alert('Este cliente no esta dentro del club. Debe tener una entrada activa antes de ir a Dispensador o Productos.');
    return false;
  };

  const openHistory = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowHistory(true);
  };

  const goDispenser = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireClientInVenue()) return;
    navigate(`/dispenser?userId=${client.id}`);
    handleModal();
  };

  const goProductos = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireClientInVenue()) return;
    navigate(`/dispenser?mode=productos&userId=${client.id}`);
    handleModal();
  };

  const openCredit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddCredit) onAddCredit();
  };

  const openEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) onEdit();
    handleModal();
  };

  const confirmDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) {
      if (onDelete) onDelete();
      handleModal();
    }
  };

  const actionBtnClass = 'inline-flex items-center justify-center gap-2';
  const mobileActionBtnClass = `${actionBtnClass} w-full min-h-[44px] rounded-xl text-sm font-semibold shadow-sm active:scale-[0.98] transition-transform`;

  return (
    <CustomModal
      active={active}
      handleModal={closeOrBack}
      title={modalTitle}
      size="wide"
      titleId="client-detail-modal-title"
    >
      <div className="wa-form space-y-6">
        {showHistory ? (
          <div className="min-h-[320px]">
            <div className="mb-4 rounded-xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
              <p className="text-base font-semibold text-[var(--wa-foreground)]">
                {client.profile?.first_name || client.profile?.last_name
                  ? `${client.profile?.first_name || ''} ${client.profile?.last_name || ''}`.trim()
                  : (client.email || 'Socio')}
              </p>
              <p className="mt-1 text-sm text-[var(--wa-muted-foreground)]">
                Nº Socio: {client.membership_id ?? '-'}
              </p>
              {client.legacy_member_id && (
                <p className="mt-0.5 text-sm text-[var(--wa-muted-foreground)]">
                  Legacy Nº Socio: <span className="font-medium">{client.legacy_member_id}</span>
                </p>
              )}
            </div>
            {loadingTransactions ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-[var(--wa-muted-foreground)]">Cargando transacciones…</p>
              </div>
            ) : (
              <ClientTransactions transactions={transactions} clientId={client?.id} />
            )}
          </div>
        ) : (
          <>
            {/* Cabecera Figma: degradado verde + avatar + estado */}
            <div className="overflow-hidden rounded-2xl bg-[linear-gradient(135deg,_#0f172a,_#14532d_55%,_#22c55e)] p-5 text-white flex items-center gap-4">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={displayName}
                  className="w-20 h-20 rounded-full object-cover border-2 border-white/40 shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const sib = e.target.nextElementSibling;
                    if (sib) { sib.classList.remove('hidden'); sib.classList.add('flex'); }
                  }}
                />
              ) : null}
              <div
                className={`w-20 h-20 rounded-full shrink-0 items-center justify-center text-white font-bold text-2xl bg-white/15 border border-white/30 ${imageUrl ? 'hidden' : 'flex'}`}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight break-words">{displayName}</h3>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-lime-50">
                  <span>Nº {client.membership_id ?? '-'}</span>
                  <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium">{estado}</span>
                  {client.legacy_member_id && <span className="text-xs text-lime-100/80">Legacy {client.legacy_member_id}</span>}
                </div>
                <p className="text-xs text-lime-100/80 break-all mt-1">{client.email}</p>
              </div>
            </div>

            {/* Saldo disponible (Figma) */}
            <div className="rounded-2xl border border-[var(--wa-border)] bg-white shadow-sm px-5 py-4 text-center">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">Saldo disponible</div>
              <div className="mt-1 text-3xl font-bold text-gray-900">€{(Number(client.profile?.credit_balance) || 0).toFixed(2)}</div>
            </div>

            <section>
              <h4 className="text-sm font-semibold text-[var(--wa-foreground)] mb-3">Datos Personales</h4>
              <div className="wa-form-grid-cols-4">
                <div className="min-w-0">
                  <span className="wa-detail-label">Nombre</span>
                  <p className="wa-detail-value">{client.profile?.first_name || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Apellidos</span>
                  <p className="wa-detail-value">{client.profile?.last_name || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">DNI</span>
                  <p className="wa-detail-value">{client.profile?.national_id ?? '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Fecha de Nacimiento</span>
                  <p className="wa-detail-value">{formatDate(client.profile?.birth_date)}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Código Postal</span>
                  <p className="wa-detail-value">{client.profile?.postal_code ?? '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Ciudad</span>
                  <p className="wa-detail-value">{client.profile?.city || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Dirección</span>
                  <p className="wa-detail-value">{client.profile?.address || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Teléfono</span>
                  <p className="wa-detail-value">{client.profile?.phone ?? '-'}</p>
                </div>
              </div>
            </section>

            <section className="border-t border-[var(--wa-border)] pt-5">
              <h4 className="text-sm font-semibold text-[var(--wa-foreground)] mb-3">Datos de Membresía</h4>
              <div className="wa-form-grid-cols-4">
                <div className="min-w-0">
                  <span className="wa-detail-label">Fecha de Inscripción</span>
                  <p className="wa-detail-value">{formatDate(client.profile?.enrollment_date)}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Fecha de Caducidad</span>
                  <p className="wa-detail-value">{formatDate(client.profile?.expiration_date)}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Socio Avalado</span>
                  <p className="wa-detail-value">{client.profile?.guarantor_member || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Línea de Coste</span>
                  <p className="wa-detail-value">{client.profile?.cost_line || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Descuento</span>
                  <p className="wa-detail-value">{client.profile?.discount || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Cuota</span>
                  <p className="wa-detail-value">{client.profile?.fee || '-'}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Consumo Máximo/Mes</span>
                  <p className="wa-detail-value">{client.profile?.max_monthly_consumption ?? 0}</p>
                </div>
                <div className="min-w-0">
                  <span className="wa-detail-label">Tipo de Consumo</span>
                  <p className="wa-detail-value">{consumptionLabel()}</p>
                </div>
              </div>
            </section>

            <section className="border-t border-[var(--wa-border)] pt-5">
              <h4 className="text-sm font-semibold text-[var(--wa-foreground)] mb-3">Notas</h4>
              <div className="rounded-2xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
                <p className="text-sm leading-6 text-[var(--wa-foreground)] whitespace-pre-wrap">
                  {client.profile?.notes?.trim() || 'Sin notas registradas.'}
                </p>
              </div>
            </section>

            <div className="border-t border-[var(--wa-border)] pt-5">
              {/* Móvil: rejilla táctil */}
              <div className="md:hidden space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 text-center">Acciones rápidas</p>
                <div className="grid grid-cols-2 gap-2.5">
                  <button type="button" onClick={openHistory} className={`${mobileActionBtnClass} wa-btn-historial`} title="Ver historial de movimientos">
                    <HiOutlineClock className="text-lg shrink-0" />
                    <span>Historial</span>
                  </button>
                  <button type="button" onClick={openCredit} className={`${mobileActionBtnClass} wa-btn-credito`} title="Agregar crédito">
                    <MdCreditCard className="text-lg shrink-0" />
                    <span>Crédito</span>
                  </button>
                  <button type="button" onClick={goDispenser} className={`${mobileActionBtnClass} wa-btn-dispensador`} title="Ir al dispensador">
                    <GiHemp className="text-lg shrink-0" />
                    <span>Dispensador</span>
                  </button>
                  <button type="button" onClick={goProductos} className={`${mobileActionBtnClass} wa-btn-dispensador`} title="Ir a productos">
                    <BsCart4 className="text-lg shrink-0" />
                    <span>Productos</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2.5 pt-1">
                  <button type="button" onClick={openEdit} className={`${mobileActionBtnClass} wa-btn-primary`} title="Editar cliente">
                    <FaEdit className="text-lg shrink-0" />
                    <span>Editar socio</span>
                  </button>
                  <button type="button" onClick={confirmDelete} className={`${mobileActionBtnClass} wa-btn-danger`} title="Eliminar cliente">
                    <FaDeleteLeft className="text-lg shrink-0" />
                    <span>Eliminar socio</span>
                  </button>
                </div>
              </div>

              {/* Tablet / PC: fila flexible (sin cambios) */}
              <div className="hidden md:flex flex-wrap justify-center gap-2 sm:gap-3">
                <button type="button" onClick={openHistory} className={`${actionBtnClass} wa-btn-historial`} title="Ver historial de movimientos">
                  <HiOutlineClock className="text-lg shrink-0" />
                  <span>Historial</span>
                </button>
                <button type="button" onClick={goDispenser} className={`${actionBtnClass} wa-btn-dispensador`} title="Ir al dispensador">
                  <GiHemp className="text-lg shrink-0" />
                  <span>Dispensador</span>
                </button>
                <button type="button" onClick={goProductos} className={`${actionBtnClass} wa-btn-dispensador`} title="Ir a productos">
                  <BsCart4 className="text-lg shrink-0" />
                  <span>Productos</span>
                </button>
                <button type="button" onClick={openCredit} className={`${actionBtnClass} wa-btn-credito`} title="Agregar crédito">
                  <MdCreditCard className="text-lg shrink-0" />
                  <span>Crédito</span>
                </button>
                <button type="button" onClick={openEdit} className={`${actionBtnClass} wa-btn-primary px-4`} title="Editar cliente">
                  <FaEdit className="text-lg shrink-0" />
                  <span>Editar</span>
                </button>
                <button type="button" onClick={confirmDelete} className={`${actionBtnClass} wa-btn-danger px-4`} title="Eliminar cliente">
                  <FaDeleteLeft className="text-lg shrink-0" />
                  <span>Eliminar</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
}

export default ClientDetailModal;
